import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDotBotsAuth } from '@dotbots-boutique/auth-sdk/react';
import { useApi } from '../lib/api';
import { hasPermission } from '../permissions';
import { t } from '../lib/i18n';
import CampaignWizard from '../components/CampaignWizard';
import type { Campaign } from '../lib/types';

const KNOWN_STATUSES = ['draft', 'active', 'paused', 'ended'];

function statusLabel(status: string): string {
  return KNOWN_STATUSES.includes(status) ? t(`status.${status}`) : status;
}

export default function Dashboard() {
  const api = useApi();
  const { user } = useDotBotsAuth();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showWizard, setShowWizard] = useState(false);

  const canWrite = hasPermission(user?.roles, 'campaigns.write');

  async function load() {
    setLoading(true);
    try {
      const res = await api<{ campaigns: Campaign[] }>('/api/campaigns');
      setCampaigns(res.campaigns);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div className="flex-between" style={{ marginBottom: '1rem' }}>
        <h1 style={{ margin: 0 }}>{t('dashboard.title')}</h1>
        {canWrite && (
          <button onClick={() => setShowWizard(true)}>{t('dashboard.new')}</button>
        )}
      </div>

      {error && <div className="error">{error}</div>}
      {loading && <div className="muted">{t('common.loading')}</div>}

      {!loading && campaigns.length === 0 && (
        <div className="card">
          <p className="muted">{t('dashboard.empty')}</p>
        </div>
      )}

      {campaigns.length > 0 && (
        <div className="card">
          <table className="responsive-table">
            <thead>
              <tr>
                <th>{t('col.name')}</th>
                <th>{t('col.status')}</th>
                <th>{t('col.trigger')}</th>
                <th style={{ textAlign: 'right' }}>{t('col.signups')}</th>
                <th style={{ textAlign: 'right' }}>{t('col.activated')}</th>
                <th style={{ textAlign: 'right' }}>{t('col.costToDate')}</th>
                <th>{t('col.endsAt')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.id}>
                  <td data-label={t('col.name')}>
                    <span>
                      <Link to={`/campaigns/${c.id}`}>{c.name}</Link>
                      <div className="small muted">{c.product_name}</div>
                    </span>
                  </td>
                  <td data-label={t('col.status')}>
                    <span className={`badge ${c.status}`}>{statusLabel(c.status)}</span>
                  </td>
                  <td data-label={t('col.trigger')} className="small">
                    {c.trigger_event}
                  </td>
                  <td data-label={t('col.signups')} style={{ textAlign: 'right' }}>
                    {c.summary?.totalParticipants ?? 0}
                  </td>
                  <td data-label={t('col.activated')} style={{ textAlign: 'right' }}>
                    {c.summary?.activatedParticipants ?? 0}
                  </td>
                  <td data-label={t('col.costToDate')} style={{ textAlign: 'right' }}>
                    €{(c.summary?.realCostToDateEur ?? 0).toFixed(2)}
                  </td>
                  <td data-label={t('col.endsAt')} className="small">
                    {new Date(c.ends_at).toLocaleDateString()}
                  </td>
                  <td>
                    <Link to={`/campaigns/${c.id}`}>{t('common.detail')}</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showWizard && (
        <CampaignWizard
          onClose={() => setShowWizard(false)}
          onCreated={() => {
            setShowWizard(false);
            load();
          }}
        />
      )}
    </div>
  );
}
